import Vue from "vue"
import { get, cloneDeep } from "lodash"
import {
  getCoachingStats,
  getCurrentPayoutPeriod,
  setCoachConnectivityStatus,
  updateCoachProfile,
} from "@/api/user/coaching/coachInfo"
import { COACH_CONNECTIVITY_STATUSES } from "@/constants/coaching"
import moment from "moment-timezone"

const state = () => ({
  loadingCoachingStats: false,
  coach: null,
  currentPayoutPeriod: null,
  selectedGameInStats: null,
  coachingStats: null,
  selectedPeriod: null,
  periodOptions: ["DAILY", "WEEKLY", "MONTHLY", "YEARLY"],
  selectedChartStats: "TOTAL STUDENTS",
  coachSelectedGameId: null,
})

const getters = {
  loadingCoachingStats: state => state.loadingCoachingStats,
  getInstaProCoach: state => state.coach,
  getCurrentPayoutPeriod: state => get(state, "currentPayoutPeriod.period"),
  getCurrentPayoutPeriodBalance: state =>
    get(state, "currentPayoutPeriod.payout"),
  getSupportedGames: state => get(state, ["coach", "games"], []),
  getPlatformCutPercentage: state => 0,
  selectedGameInStats: state => state.selectedGameInStats,
  coachingStats: state => state.coachingStats,
  selectedPeriod: state => state.selectedPeriod,
  periodOptions: state => state.periodOptions,
  selectedChartStats: state => state.selectedChartStats,
  historiesData: state => ({
    "TOTAL STUDENTS": state.coachingStats.histories.map(history =>
      Number(history.student_count)
    ),
    "NEW STUDENTS": state.coachingStats.histories.map(
      history =>
        Number(history.student_count) - Number(history.return_student_count)
    ),
    "RETENTION RATE": state.coachingStats.histories.map(history =>
      Number(history.return_percentage)
    ),
    "AVG. RATING": state.coachingStats.histories.map(
      history => (Number(history.rate_average) / 100) * 5
    ),
    "SESSION TIME": state.coachingStats.histories.map(history =>
      Number(history.student_coach_time)
    ),
    "EARNINGS": state.coachingStats.histories.map(history =>
      Number(Number(history.earning).toFixed(2))
    ),
  }),
  statsChartCategories: state => {
    if (state.selectedPeriod === "MONTHLY") {
      return state.coachingStats.histories.map(history =>
        moment.utc(history.from).format("MMM YYYY")
      )
    }
    if (state.selectedPeriod === "YEARLY") {
      return state.coachingStats.histories.map(history =>
        moment.utc(history.from).format("MMM YYYY")
      )
    }
    return state.coachingStats.histories.map(history =>
      moment.utc(history.from).format("MMM DD")
    )
  },
  coachSelectedGameId: state => state.coachSelectedGameId,
}

const mutations = {
  SET_INSTAPRO_COACH: (state, coachData) => {
    Vue.set(state, "coach", coachData)
  },
  UPDATE_CURRENT_PAYOUT_PERIOD: (state, payoutPeriodData) => {
    Vue.set(state, "currentPayoutPeriod", payoutPeriodData)
  },
  setSelectedGameInStats: (state, game) => {
    Vue.set(state, "selectedGameInStats", game)
  },
  setCoachingStats: (state, stats) => (state.coachingStats = stats),
  setSelectedPeriod: (state, period) => (state.selectedPeriod = period),
  setSelectedChartStats: (state, selectedChartStats) =>
    (state.selectedChartStats = selectedChartStats),
  setLoadingCoachingStats: (state, loading) =>
    (state.loadingCoachingStats = loading),
  setCoachSelectedGameId: (state, id) => (state.coachSelectedGameId = id),
}

const actions = {
  async loadCurrentPayoutPeriod ({ state, dispatch }) {
    // Payout period data already initially loaded
    if (state.currentPayoutPeriod !== null) {
      return
    }

    return dispatch("refreshCurrentPayoutPeriod")
  },
  async refreshCurrentPayoutPeriod ({ commit, rootGetters }) {
    commit(
      "UPDATE_CURRENT_PAYOUT_PERIOD",
      await getCurrentPayoutPeriod(
        rootGetters["User/Coaching/getInstaProAccessToken"]
      )
    )
  },
  async saveInstaProCoach ({ commit }, coachData) {
    commit("SET_INSTAPRO_COACH", coachData)
  },
  async updateCoachData ({ commit, dispatch, rootGetters }, coachUpdatePayload) {
    console.log("coach updated")
    const response = await updateCoachProfile(
      rootGetters["User/Coaching/getInstaProAccessToken"],
      coachUpdatePayload
    )
    const updatedCoachData = get(response, "result")
    if (updatedCoachData) {
      await dispatch("saveInstaProCoach", updatedCoachData)
    }
  },
  async setCoachStatus ({ state, commit, rootGetters }, status) {
    if (!Object.values(COACH_CONNECTIVITY_STATUSES).includes(status)) {
      throw new Error("Invalid coach status")
    }

    await setCoachConnectivityStatus(
      rootGetters["User/Coaching/getInstaProAccessToken"],
      status
    )

    let coachData = cloneDeep(state.coach)
    if (coachData) {
      coachData.status = status
      commit("SET_INSTAPRO_COACH", coachData)
    }
  },
  async getCoachingStats ({ state, commit, rootGetters }, game) {
    commit("setLoadingCoachingStats", true)
    const instaproToken = rootGetters["User/Coaching/getInstaProAccessToken"]
    const gameId = game ? game.game_id : null
    const data = await getCoachingStats(
      instaproToken,
      gameId,
      state.selectedPeriod ? state.selectedPeriod : state.periodOptions[0]
    )
    commit("setSelectedGameInStats", game)
    commit("setCoachingStats", data.result)
    commit("setLoadingCoachingStats", false)
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}



// WEBPACK FOOTER //
// ./src/store/modules/user/coaching/coachInfo.js